import React, { useState, useEffect } from "react";
import { Trash2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableItem = ({ floor, onDeleteFloor }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: floor,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const DeleteButton = () => (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (
          window.confirm(
            `Are you sure you want to delete the floor "${floor}"? This will also remove all related assets.`
          )
        ) {
          onDeleteFloor(floor);
        }
      }}
      className="ml-2 p-1 text-gray-500 hover:text-red-500"
      title="Delete Floor"
    >
      <Trash2 size={14} />
    </button>
  );

  return (
    <div className="flex items-center space-x-1">
      <div
        ref={setNodeRef}
        style={style}
        className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 
                  rounded-md text-sm text-gray-700 hover:border-blue-200"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} className="mr-2 text-gray-400" />
        <span>{floor}</span>
      </div>

      <DeleteButton />
    </div>
  );
};

const DraggableFloorManager = ({
  floors = [],
  onDeleteFloor,
  onSaveOrder,
  loading,
}) => {
  const [items, setItems] = useState(floors);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    setItems(floors);
    setHasChanged(false);
  }, [floors]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        const newArray = arrayMove(items, oldIndex, newIndex);
        setHasChanged(true);
        return newArray;
      });
    }
  };

  const handleSaveOrder = () => {
    onSaveOrder(items);
    setHasChanged(false);
  };

  if (items.length === 0 && !loading) {
    return (
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-3 pb-2 border-b border-gray-200">
          Floor List
        </h3>
        <div className="p-4 bg-gray-50 rounded-md text-gray-500 text-sm">
          No floors available
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-800">
          <span>Floor List</span>
        </h3>

        {hasChanged && (
          <button
            onClick={handleSaveOrder}
            className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
            title="Save floor order"
          >
            Save Order
          </button>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-3">
        Drag floors to reorder them. New order will affect all assets.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items}
            strategy={horizontalListSortingStrategy}
          >
            {items.map((floor) => (
              <SortableItem
                key={floor}
                floor={floor}
                onDeleteFloor={onDeleteFloor}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default DraggableFloorManager;
